import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAccess } from "../context/AccessContext";
import { useCart } from "../context/CartContext";

export default function Tutorials() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { unlocked } = useAccess();
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/tutorials")
      .then((r) => setTutorials(r.data.tutorials))
      .finally(() => setLoading(false));
  }, []);

  const buyAndGo = (productId) => {
    addItem(productId);
    navigate("/cart");
  };

  return (
    <div data-testid="tutorials-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <header className="mb-12 lg:mb-16">
        <div className="overline mb-4">The Studio</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">
          Video Tutorials.
        </h1>
        <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">
          Six technique-focused lessons taught by working makeup artists. {unlocked ? "All tutorials are unlocked on this device." : "Included with the Tutorial Vault or the Complete 30-Piece Set."}
        </p>
      </header>

      {!unlocked && (
        <div data-testid="tutorials-paywall" className="mb-12 bg-[#2C2A29] text-stone-100 p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="overline mb-3" style={{ color: "#E8D5D1" }}>Locked</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
                Unlock all six video tutorials.
              </h2>
              <p className="mt-4 text-sm text-stone-300 leading-relaxed max-w-md">
                One purchase, lifetime access on any device. Choose digital-only or the full kit with brushes included.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#F9F7F3] text-stone-900 p-5 flex flex-col">
                <div className="overline mb-2">Digital</div>
                <div className="font-serif-display text-2xl">Tutorial Vault</div>
                <p className="text-xs text-stone-500 mt-1 flex-1">All 6 video tutorials + 30 written brush guides.</p>
                <div className="font-serif-display text-3xl mt-3">$29</div>
                <button
                  data-testid="tut-buy-vault"
                  onClick={() => buyAndGo("tutorial-vault")}
                  className="mt-4 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-5 py-3 transition-colors"
                >
                  Unlock Now
                </button>
              </div>
              <div className="bg-[#F9F7F3] text-stone-900 p-5 flex flex-col">
                <div className="overline mb-2">Best Value</div>
                <div className="font-serif-display text-2xl">Brushes + Tutorials</div>
                <p className="text-xs text-stone-500 mt-1 flex-1">The full 30-piece kit plus all tutorials.</p>
                <div className="font-serif-display text-3xl mt-3">$299</div>
                <button
                  data-testid="tut-buy-set"
                  onClick={() => buyAndGo("set-complete-30")}
                  className="mt-4 text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-5 py-3 transition-colors"
                >
                  Buy the Kit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (<div key={i} className="bg-[#F2EFEB] h-[420px] animate-pulse" />))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10" data-testid="tutorials-grid">
          {tutorials.map((t, i) => (
            <Link
              to={unlocked ? `/tutorials/${t.id}` : "#"}
              onClick={(e) => { if (!unlocked) { e.preventDefault(); } }}
              key={t.id}
              data-testid={`tutorial-card-${t.id}`}
              className={`group block bg-white border border-[#E5E0D8] ${unlocked ? "bb-card-hover" : "opacity-90"}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#F2EFEB] relative">
                <img
                  src={t.thumb}
                  alt={t.title}
                  className={`w-full h-full object-cover transition-transform duration-700 ${unlocked ? "group-hover:scale-[1.04]" : "blur-sm"}`}
                />
                {!unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-[#2C2A29] text-white text-xs uppercase tracking-[0.22em] px-4 py-2">Locked</div>
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="overline mb-2">{t.level} · {t.duration}</div>
                <h3 className="font-serif-display text-2xl text-stone-900 leading-snug">{t.title}</h3>
                <p className="mt-2 text-sm text-stone-500 italic">{t.tagline}</p>
                {unlocked && (
                  <div className="mt-4 text-xs uppercase tracking-[0.22em] text-[#8C6A5D] group-hover:text-[#70544A]">
                    {t.has_video ? "Watch →" : "Coming soon"}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
